import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getYear,
  getMonth,
  getDaysInMonth,
  getDate,
  getDay,
  subDays,
  subMonths,
  addDays,
  addMonths,
  isAfter,
  isBefore,
  format,
  isSameDay,
  startOfMonth,
  endOfMonth,
} from "date-fns";

import styles from "./date_range_picker.module.css";
import {
  isTodayOrPast,
  range,
  convertWeekIndexByLocale,
  hasUnavailbleDayInBetween,
  startOfWeek,
} from "./date_range_picker_utils";
import { DateRangePickerDate } from "./date_range_picker_date";
import { PulseLoader } from "../../../components/pulse_loader";
import { hasAvailableDateOnCurrentMonth } from "@/offering_v2/utils/has_available_day_on_current_month";
import { useHoverPreviewDateRangeFeatureFlag } from "@/core/splitio";
import classNames from "classnames";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { eachDayOfWeek } from "@/lib/datetime";
import { formatCurrency } from "@/lib/currency/currency";
import { getSystemLocale } from "@/lib/locale/locale";

type DynamicPricing = {
  [date: string]: number;
};

interface DateRangePickerValue {
  startDate?: Date;
  endDate?: Date;
}

export enum PickingRange {
  OneDate,
  Consecutive,
  NonConsecutive,
}

interface DateRangePickerProps {
  availableDates?: Date[];
  basePrice?: number;
  dynamicPricings?: DynamicPricing;
  value?: DateRangePickerValue;
  onChange?: (value: { startDate: Date; endDate: Date }) => void;
  onViewDates?: (startDate: Date, endDate: Date, viewingDate: Date) => void;
  currency?: string;
  isLoading?: boolean;
  hidePrice?: boolean;
  pickingRange: PickingRange;
  partnerId?: string;
  displayPlus?: boolean;
  disableSelectPastMonth?: boolean;
}

const today = new Date();

export function DateRangePicker({
  availableDates,
  basePrice,
  dynamicPricings,
  value,
  currency,
  onChange,
  onViewDates,
  isLoading,
  hidePrice,
  pickingRange,
  partnerId,
  displayPlus,
  disableSelectPastMonth,
}: DateRangePickerProps) {
  const isHoverPreviewDateRangeEnabled = useHoverPreviewDateRangeFeatureFlag(
    partnerId || ""
  );
  const { fbt } = useI18n();

  const startDateValue = value?.startDate;
  const endDateValue = value?.endDate;
  const [dateObj, setDateObj] = useState(startDateValue || today);
  const [hoveredEndDate, setHoveredEndDate] = useState<Date>();

  /**
   * We would like to stop auto changing month view of the calendar after
   * it has been done one time. Because user should be able to click next and prev
   * and we shouldnt interfere after those actions even if the month doesnt have the available day
   * Because user has done it on purpose.
   */
  const hasViewedMonthChangedRef = useRef(false);
  const thisYear = getYear(dateObj);
  const thisMonth = getMonth(dateObj); // (January as 0, December as 11)
  const daysInMonth = getDaysInMonth(dateObj);

  const firstDateInMonth = new Date(thisYear, thisMonth, 1);
  const weekDayOf1 = convertWeekIndexByLocale(getDay(firstDateInMonth)); // (Sunday as 0, Saturday as 6)

  const dateObjOfLast = new Date(thisYear, thisMonth, daysInMonth);
  const weekDayOfLast = convertWeekIndexByLocale(getDay(dateObjOfLast));

  const isPrevMonthDisabled =
    disableSelectPastMonth && startOfMonth(dateObj) <= startOfMonth(today);

  const handleViewDates = useCallback(
    (viewingDate: Date) => {
      if (pickingRange === PickingRange.OneDate) {
        onViewDates?.(
          startOfMonth(viewingDate),
          endOfMonth(viewingDate),
          viewingDate
        );
      } else {
        onViewDates?.(
          startOfMonth(
            // if start date is in Jul and user move calendar to Aug, we want to get available dates from Jul
            startDateValue && isAfter(viewingDate, startDateValue)
              ? startDateValue
              : viewingDate
          ),
          endOfMonth(
            // if end date is in Jul and user move calendar to Jun, we want to get available dates till Jul
            endDateValue && isBefore(viewingDate, endDateValue)
              ? endDateValue
              : viewingDate
          ),
          viewingDate
        );
      }
    },
    [startDateValue, endDateValue, onViewDates, pickingRange]
  );

  const handlePrev = useCallback(() => {
    const prevMonth = subMonths(dateObj, 1);
    setDateObj(prevMonth);
    handleViewDates(prevMonth);
    hasViewedMonthChangedRef.current = true;
  }, [dateObj, handleViewDates]);

  const handleNext = useCallback(() => {
    const nextMonth = addMonths(dateObj, 1);
    setDateObj(nextMonth);
    handleViewDates(nextMonth);
    hasViewedMonthChangedRef.current = true;
  }, [dateObj, handleViewDates]);

  const isClickedDate = (date: Date): boolean => {
    return (
      (!!startDateValue && isSameDay(startDateValue, date)) ||
      (!!endDateValue && isSameDay(endDateValue, date))
    );
  };

  const isUnavailbleDate = (date: Date) => {
    return !availableDates?.some((d) => isSameDay(d, date));
  };

  const isEndDateAvailable = (date: Date): boolean => {
    if (!startDateValue) {
      return false;
    }

    const allowRangeSelection =
      (pickingRange === PickingRange.Consecutive &&
        !hasUnavailbleDayInBetween(startDateValue, date, availableDates)) ||
      pickingRange === PickingRange.NonConsecutive;
    const endDateAvailable =
      (!endDateValue || isSameDay(startDateValue, endDateValue)) &&
      isAfter(date, startDateValue) &&
      allowRangeSelection;
    return endDateAvailable;
  };

  const handleClickDate = (date: Date) => {
    if (isTodayOrPast(date) || isUnavailbleDate(date)) {
      return;
    }

    if (pickingRange === PickingRange.OneDate) {
      onChange?.({ startDate: date, endDate: date });
      return;
    }

    if (startDateValue && isEndDateAvailable(date)) {
      onChange?.({ startDate: startDateValue, endDate: date });
    } else {
      onChange?.({ startDate: date, endDate: date });
    }
  };

  const handleMouseEnterDate = (date: Date) => {
    if (isUnavailbleDate(date) || !isHoverPreviewDateRangeEnabled) {
      setHoveredEndDate(undefined);
      return;
    }

    if (isEndDateAvailable(date)) {
      setHoveredEndDate(date);
    } else {
      setHoveredEndDate(undefined);
    }
  };

  const hasDateRange =
    !!startDateValue &&
    !!endDateValue &&
    !isSameDay(startDateValue, endDateValue);

  const isDayInBetween = (date: Date) => {
    if (startDateValue && endDateValue && hasDateRange) {
      return isAfter(date, startDateValue) && isBefore(date, endDateValue);
    }

    if (startDateValue && hoveredEndDate) {
      return isAfter(date, startDateValue) && isBefore(date, hoveredEndDate);
    }

    return false;
  };

  useEffect(() => {
    if (
      availableDates &&
      !hasAvailableDateOnCurrentMonth({
        availableDates,
        currentDate: dateObj,
      }) &&
      isSameDay(dateObj, today) &&
      !hasViewedMonthChangedRef.current
    ) {
      handleNext();
      hasViewedMonthChangedRef.current = true;
    }
  }, [startDateValue, availableDates, dateObj, handleNext]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.dateTimeLoadingWrapper}>
          <div className={styles.datetime}>
            {fbt.formatDate(dateObj, { month: "long", year: "numeric" })}
          </div>
          {isLoading && <PulseLoader size={12} />}
        </div>
        <div className={styles.navsWrapper}>
          <button
            type="button"
            data-testid="date-range-picker-nav-pre-month"
            className={classNames(styles.nav, "calendar_left_nav")}
            onClick={handlePrev}
            disabled={isPrevMonthDisabled}
          >
            <ArrowLeft />
          </button>
          <button
            type="button"
            data-testid="date-range-picker-nav-next-month"
            className={classNames(styles.nav, "calendar_right_nav")}
            onClick={handleNext}
          >
            <ArrowRight />
          </button>
        </div>
      </div>

      <div className={styles.weekContainer}>
        {eachDayOfWeek(today, startOfWeek()).map((d, i) => (
          <div className={styles.weekCell} key={i}>
            {fbt.formatDateByPattern(d, "eeeee")}
          </div>
        ))}
      </div>

      <div className={styles.dayContainer}>
        {range(weekDayOf1).map((i) => (
          <div className={styles.dayCellWrapper} key={i}>
            <div className={`${styles.dayCell} ${styles.dayCellHidden}`}>
              {getDate(subDays(firstDateInMonth, weekDayOf1 - i))}
            </div>
          </div>
        ))}

        {range(daysInMonth).map((i) => {
          const current = new Date(thisYear, thisMonth, i + 1);
          const dynamicPricing =
            dynamicPricings?.[format(current, "yyyy-MM-dd")];
          const isHoveredEndDate =
            !!hoveredEndDate && isSameDay(hoveredEndDate, current);
          const hasHoverDateRange =
            !!startDateValue &&
            !!hoveredEndDate &&
            isAfter(hoveredEndDate, startDateValue);
          const displayPrice = dynamicPricing || basePrice;
          return (
            <DateRangePickerDate
              key={i}
              date={i + 1}
              isDateInBetween={isDayInBetween(current)}
              isHighlighted={isClickedDate(current) || isHoveredEndDate}
              isDateFaded={isTodayOrPast(current) || isUnavailbleDate(current)}
              price={
                displayPrice && currency
                  ? formatCurrency(
                      Math.round(displayPrice),
                      currency,
                      getSystemLocale()
                    ) + (displayPlus ? "+" : "")
                  : undefined
              }
              isDiscountPrice={Number(dynamicPricing) < Number(basePrice)}
              isSameFirstDate={
                (hasDateRange || hasHoverDateRange) &&
                !!startDateValue &&
                isSameDay(startDateValue, current)
              }
              isSameSecondDate={
                (hasDateRange &&
                  !!endDateValue &&
                  isSameDay(endDateValue, current)) ||
                isHoveredEndDate
              }
              onClick={() => handleClickDate(current)}
              showPrice={!hidePrice}
              onMouseEnter={() => handleMouseEnterDate(current)}
              onMouseLeave={() => setHoveredEndDate(undefined)}
              isLoading={!availableDates}
            />
          );
        })}

        {range(6 - weekDayOfLast).map((i) => (
          <div className={styles.dayCellWrapper} key={i}>
            <div className={`${styles.dayCell} ${styles.dayCellHidden}`}>
              {getDate(addDays(dateObjOfLast, i + 1))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArrowLeft() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M16.1189 21.2459C16.0559 21.246 15.9934 21.2336 15.9352 21.2094C15.877 21.1853 15.8241 21.1499 15.7796 21.1053L7.13956 12.4653C7.04957 12.3752 6.99902 12.2532 6.99902 12.1259C6.99902 11.9986 7.04957 11.8766 7.13956 11.7865L15.7796 3.14654C15.8238 3.10069 15.8768 3.06413 15.9354 3.03897C15.9939 3.01381 16.0569 3.00057 16.1206 3.00002C16.1844 2.99946 16.2476 3.01161 16.3066 3.03574C16.3656 3.05988 16.4192 3.09552 16.4642 3.14059C16.5093 3.18566 16.5449 3.23925 16.5691 3.29824C16.5932 3.35723 16.6054 3.42044 16.6048 3.48417C16.6042 3.5479 16.591 3.61089 16.5658 3.66945C16.5407 3.72801 16.5041 3.78098 16.4583 3.82526L8.15764 12.1259L16.4583 20.4265C16.5254 20.4937 16.5711 20.5792 16.5896 20.6723C16.6081 20.7654 16.5986 20.8619 16.5623 20.9496C16.526 21.0373 16.4645 21.1122 16.3855 21.165C16.3066 21.2177 16.2138 21.2459 16.1189 21.2459Z" />
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M7.4799 21.2341C7.54294 21.2342 7.60537 21.2218 7.66361 21.1976C7.72185 21.1735 7.77475 21.1381 7.81926 21.0935L16.4593 12.4535C16.5492 12.3634 16.5998 12.2414 16.5998 12.1141C16.5998 11.9868 16.5492 11.8648 16.4593 11.7747L7.81926 3.13474C7.72873 3.04731 7.60748 2.99892 7.48163 3.00002C7.35577 3.00111 7.23538 3.05159 7.14638 3.14059C7.05739 3.22958 7.00691 3.34997 7.00581 3.47583C7.00472 3.60168 7.0531 3.72293 7.14054 3.81346L15.4412 12.1141L7.14054 20.4147C7.07343 20.4819 7.02773 20.5674 7.00922 20.6605C6.99071 20.7536 7.00021 20.8501 7.03653 20.9378C7.07285 21.0255 7.13436 21.1004 7.21328 21.1532C7.29219 21.2059 7.38498 21.2341 7.4799 21.2341Z" />
    </svg>
  );
}
